// src/components/LoginForm.jsx
import { useState } from "react";
import {
  adminLogin,
  employeeLogin,
  getSavedLogin,
  saveLogin,
  clearSavedLogin,
} from "../services/authService";
import "./LoginForm.css";

// 社員番号・パスワードの自動入力（社員ログインのみ）。
// 共有端末では前に使った人のパスワードが残るため、チェックを外して使う運用が前提。
const REMEMBER_KEY = "rememberLogin:employee";

export default function LoginForm({ onSuccess, title, accent="#2563eb", mode="employee" }) {
  const canRemember = mode === "employee";
  const [saved] = useState(() => (canRemember ? getSavedLogin() : null));
  const [username, setUsername] = useState(saved?.username ?? "");
  const [password, setPassword] = useState(saved?.password ?? "");
  // 自動入力されたパスワードは「表示」できないようにする（他人に読まれるのを防ぐ）。
  // 利用者が自分で入力し直した時点で表示できるようになる。
  const [pwRestored, setPwRestored] = useState(!!saved?.password);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  // 社員ログインは既定ON（未設定なら保持する）。管理者ログインは従来どおり既定OFF。
  const [rememberMe, setRememberMe] = useState(() => {
    if (!canRemember) return false;
    try { return localStorage.getItem(REMEMBER_KEY) !== "0"; } catch (_) { return true; }
  });
  const [showForgotModal, setShowForgotModal] = useState(false);

  function onChangeRemember(checked) {
    setRememberMe(checked);
    if (!canRemember) return;
    try { localStorage.setItem(REMEMBER_KEY, checked ? "1" : "0"); } catch (_) {}
    if (!checked) clearSavedLogin();
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setErr(""); setLoading(true);
    const id = typeof username === "string" ? username.trim() : "";
    const pass = typeof password === "string" ? password.trim() : "";
    if (!id || !pass) {
      setErr("ユーザー名（社員番号）とパスワードを入力してください。");
      setLoading(false);
      return;
    }
    try {
      const user = mode === "admin"
        ? await adminLogin(id, pass, rememberMe)
        : await employeeLogin(id, pass, rememberMe);
      if (canRemember) {
        if (rememberMe) saveLogin(id, pass);
        else clearSavedLogin();
      }
      onSuccess?.(user);
    } catch (error) {
      let message = "ログインに失敗しました。";
      if (error?.message === "NOT_ADMIN") {
        message = "管理者権限がありません。";
      } else if (error?.response?.data?.non_field_errors?.length > 0) {
        message = error.response.data.non_field_errors[0];
      } else if (error?.response?.status === 401 || error?.response?.status === 400) {
        message = "ユーザー名またはパスワードが正しくありません。";
        // 管理者がパスワードをリセットすると保存値が古いままになる。
        // 失敗したパスワードは破棄し、社員番号だけ残して入力し直してもらう。
        if (canRemember) {
          saveLogin(id, "");
          setPassword("");
          setPwRestored(false);
        }
      } else if (error?.response?.status === 429) {
        message = "ログイン試行回数が上限に達しました。しばらく待ってから再度お試しください。";
      } else if (error?.response?.status >= 500) {
        message = "サーバーエラーが発生しました。しばらく待ってから再度お試しください。";
      } else if (typeof navigator !== "undefined" && !navigator.onLine) {
        message = "ネットワーク接続を確認してください。";
      }
      setErr(message);
    } finally { setLoading(false); }
  }

  return (
    <div className="login-container">
      <form onSubmit={handleSubmit} className="login-form">
        <h1 className="form-title" style={{ color: accent }}>{title}</h1>

        <label className="input-label">{mode === "employee" ? "社員番号" : "ユーザー名"}</label>
        <input
          className="form-input"
          name={mode === "employee" ? "memberId" : "username"}
          autoComplete={mode === "employee" ? "username" : "username"}
          value={username}
          onChange={e=>setUsername(e.target.value)}
          placeholder={mode === "employee" ? "例: S0001" : ""}
          required
        />

        <label className="input-label">パスワード</label>
        <div className="password-input-container">
          <input
            className="form-input password-input"
            name="password"
            autoComplete="current-password"
            type={(show && !pwRestored) ? "text" : "password"}
            value={password}
            onChange={e=>{ setPassword(e.target.value); if (pwRestored) setPwRestored(false); }}
            required
          />
          {!pwRestored && (
            <button type="button" onClick={()=>setShow(!show)} className="show-password-btn">
              {show ? "隠す" : "表示"}
            </button>
          )}
        </div>

        <div className="remember-row">
          <label className="remember-label">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={e => onChangeRemember(e.target.checked)}
              className="remember-checkbox"
            />
            {canRemember ? "ログイン状態と社員番号・パスワードを保持する" : "ログイン状態を保持する"}
          </label>
          <button type="button" className="forgot-link" onClick={() => setShowForgotModal(true)}>
            パスワードを忘れた場合
          </button>
        </div>

        {err && <div className="error-message">{err}</div>}
        <button disabled={loading} className="login-btn" style={{ background: accent }}>
          {loading ? "ログイン中・・・" : "ログイン"}
        </button>

        <p className="info-message">
          {canRemember
            ? "チェックを入れると次回から社員番号・パスワードを自動入力します。共有端末ではチェックを外してください。"
            : "一定時間操作がない場合、自動的にサインアウトします。"}
        </p>
      </form>

      {showForgotModal && (
        <div className="modal-overlay" onClick={() => setShowForgotModal(false)}>
          <div className="forgot-modal" onClick={e => e.stopPropagation()}>
            <h3 className="forgot-modal-title">パスワードを忘れた場合</h3>
            <p className="forgot-modal-body">
              パスワードのリセットは管理者にお問い合わせください。
            </p>
            <p className="forgot-modal-contact">
              管理者に連絡して、パスワードをリセットしてもらってください。
            </p>
            <button
              className="forgot-close-btn"
              onClick={() => setShowForgotModal(false)}
            >
              閉じる
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
